import React, { useEffect } from 'react';
import { tr } from '../../i18n/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchNotifications, markNotificationsAsRead } from '../../redux/action/notification-log.action';
import { showNotification } from '../../redux/slices/notification.slice';
import { INotificationItem, NotificationType } from '../../interface/notification-log.interface';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import {
  getNotificationStyle,
  mapScreenToRoute,
  mapNotificationTypeToRoute,
  formatNotificationTime,
} from '../../utility/notification';

interface NotificationPanelProps {
  onNavigate: (route: string) => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { notifications = [], totalUnread, isLoading } = useSelector(
    (state: RootState) => state.notificationLog
  );

  // Fetch notifications when panel opens
  useEffect(() => {
    dispatch(fetchNotifications({}));
  }, [dispatch]);

  // Mark all as read
  const handleMarkAllRead = () => {
    const unreadIds = notifications
      .filter((n: INotificationItem) => !n.isRead)
      .map((n: INotificationItem) => n.id);
    if (unreadIds.length > 0) {
      dispatch(markNotificationsAsRead({ notificationIds: unreadIds }))
        .unwrap()
        .then(() => dispatch(fetchNotifications({}))) // Refresh after marking
        .catch((error: any) => {
          dispatch(
            showNotification({
              message: error?.message || tr('g.failed_to_mark_notifications_as_read', 'Failed to mark notifications as read'),
              severity: 'error',
            }),
          );
        });
    }
  };

  // Navigate immediately, mark as read in background
  const handleNotificationClick = (notification: INotificationItem) => {
    // Determine route first (prefer type mapping, then fallback to screen mapping)
    const typeRoute = mapNotificationTypeToRoute(notification.type as NotificationType);
    const routeToNavigate =
      typeRoute ||
      (notification.metaData?.screen ? mapScreenToRoute(notification.metaData.screen) : null);

    // Navigate immediately and close panel
    if (routeToNavigate) {
      onNavigate(routeToNavigate);
    }

    // Mark as read in background (don't wait for it)
    if (!notification.isRead) {
      dispatch(markNotificationsAsRead({ notificationIds: [notification.id] }))
        .unwrap()
        .then(() => dispatch(fetchNotifications({})))
        .catch((error: any) => {
          dispatch(
            showNotification({
              message: error?.message || tr('g.failed_to_mark_notification_as_read', 'Failed to mark notification as read'),
              severity: 'error',
            }),
          );
        });
    }
  };

  // Render notification icon with type-based styling
  const renderNotificationIcon = (type: string) => {
    const style = getNotificationStyle(type as NotificationType);
    return (
      <div
        className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${style.bgColor}`}
      >
        <i className={`${style.icon} ${style.iconColor} text-lg`} />
      </div>
    );
  };

  return (
    <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ backgroundColor: 'var(--secondary-bg-color)' }}
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold text-lg text-gray-800">{t('common.notifications', 'Notifications')}</span>
          {totalUnread > 0 && (
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full text-white"
              style={{ backgroundColor: 'var(--primary-color)' }}
            >
              {totalUnread} new
            </span>
          )}
        </div>
        {totalUnread > 0 && (
          <button
            className="text-sm font-medium hover:underline transition-colors"
            style={{ color: 'var(--primary-color)' }}
            onClick={handleMarkAllRead}
            disabled={isLoading}
          >{t('g.mark_all_as_read', 'Mark all as read')}</button>
        )}
      </div>

      {/* Notification List */}
      <div className="max-h-[28rem] overflow-y-auto">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="px-4 py-3 flex items-start gap-3">
                {/* Icon skeleton */}
                <Skeleton shape="circle" size="40px" />
                {/* Content skeleton */}
                <div className="flex-1 min-w-0">
                  <Skeleton width="70%" height="14px" className="mb-2" />
                  <Skeleton width="100%" height="12px" className="mb-1" />
                  <Skeleton width="80%" height="12px" className="mb-2" />
                  <Skeleton width="40%" height="10px" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500">
            <i className="pi pi-bell-slash text-4xl mb-2 text-gray-300" />
            <span className="text-sm">{t('common.no_notifications', 'No new notifications yet')}</span>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification: INotificationItem) => (
              <button
                key={notification.id}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 focus:outline-none transition-colors ${
                  notification.isRead ? 'bg-white' : 'bg-purple-50/50'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                {/* Icon */}
                {renderNotificationIcon(notification.type)}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={`font-medium text-sm truncate ${
                        notification.isRead ? 'text-gray-700' : 'text-gray-900'
                      }`}
                    >
                      {notification.title}
                    </span>
                    {!notification.isRead && (
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                        style={{ backgroundColor: 'var(--primary-color)' }}
                      />
                    )}
                  </div>
                  <p
                    className={`text-sm mt-0.5 line-clamp-2 ${
                      notification.isRead ? 'text-gray-500' : 'text-gray-600'
                    }`}
                  >
                    {notification.message}
                  </p>
                  <span className="text-xs text-gray-400 mt-1 block">
                    {formatNotificationTime(notification.createdAt)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer - View All (if needed in future) */}
      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
          <p className="text-xs text-center text-gray-400">
            Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
